import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../Input/Input";
import Button from "../../Button/Button";
import Modal from "../../Modal/Modal";
import Notification from "../../Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./LosCredentials.module.scss";

const ipRegex =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const CreateLosCredential = ({ onBack, isAdmin = false }) => {
  /* =========================
     STATE
  ========================= */
  const [keyName, setKeyName] = useState("");
  // Internal users select the org from a dropdown; regular users get their own
  // company name (read-only) from localStorage.
  const [companyName, setCompanyName] = useState(
    isAdmin ? "" : localStorage.getItem("companyName") || "",
  );
  const [orgOptions, setOrgOptions] = useState([]);
  const [expiry, setExpiry] = useState("");

  // Products
  const [productOptions, setProductOptions] = useState([]);
  const [productPick, setProductPick] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);

  // IP whitelist
  const [ipInput, setIpInput] = useState("");
  const [ipList, setIpList] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Secret-reveal modal (shown once after creation)
  const [createdModal, setCreatedModal] = useState(null);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     FETCH PRODUCTS
  ========================= */
  const fetchProducts = useCallback(async () => {
    try {
      const res = await apiGet("catalog", "/apimaster/products/dropdown");
      if (res?.status === "success" && Array.isArray(res.data)) {
        setProductOptions(
          res.data.map((p) => ({ label: p.name, value: String(p.id) })),
        );
      }
    } catch (err) {
      console.error("Failed to load products", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  /* =========================
     FETCH ORGANIZATIONS (internal users only)
  ========================= */
  const fetchOrganizations = useCallback(async () => {
    try {
      const res = await apiGet("user", "/organization/dropdown");
      if (res?.status === "success" && Array.isArray(res.data)) {
        setOrgOptions(
          res.data.map((o) => ({ label: o.companyName, value: o.companyName })),
        );
      }
    } catch (err) {
      console.error("Failed to load organizations", err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchOrganizations();
  }, [isAdmin, fetchOrganizations]);

  /* =========================
     PRODUCTS HELPERS
  ========================= */
  const addProduct = () => {
    if (!productPick) return;
    if (selectedProducts.some((p) => p.value === productPick)) return;
    const opt = productOptions.find((p) => p.value === productPick);
    if (opt) {
      setSelectedProducts((prev) => [...prev, opt]);
      setProductPick("");
      setErrors((prev) => ({ ...prev, products: "" }));
    }
  };

  const removeProduct = (value) => {
    setSelectedProducts((prev) => prev.filter((p) => p.value !== value));
  };

  /* =========================
     IP HELPERS
  ========================= */
  const addIp = () => {
    const trimmed = ipInput.trim();
    setErrors((prev) => ({ ...prev, ipWhitelist: "" }));

    if (!trimmed) return;
    if (!ipRegex.test(trimmed)) {
      setErrors((prev) => ({ ...prev, ipWhitelist: "Enter a valid IP Address" }));
      return;
    }
    if (ipList.includes(trimmed)) {
      setErrors((prev) => ({ ...prev, ipWhitelist: "IP already added" }));
      return;
    }
    setIpList((prev) => [...prev, trimmed]);
    setIpInput("");
  };

  const removeIp = (ip) => setIpList((prev) => prev.filter((i) => i !== ip));

  /* =========================
     CREATE
  ========================= */
  const handleCreate = async () => {
    const next = {};

    // Fold a pending IP in the input into the list.
    const finalIps = [...ipList];
    if (ipInput.trim()) {
      if (!ipRegex.test(ipInput.trim())) {
        next.ipWhitelist = "Enter a valid IP Address";
      } else if (!finalIps.includes(ipInput.trim())) {
        finalIps.push(ipInput.trim());
      }
    }

    if (!keyName.trim()) next.keyName = "Key Name is required";
    if (isAdmin && !companyName.trim())
      next.companyName = "Please select a company";
    if (selectedProducts.length === 0)
      next.products = "Select at least one product";
    if (finalIps.length === 0) next.ipWhitelist = "Add at least one IP address";

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    const payload = {
      keyName: keyName.trim(),
      productIds: selectedProducts.map((p) => Number(p.value)),
      ipWhitelist: finalIps,
      companyName: companyName.trim(),
    };
    // Expiry is optional; send an ISO timestamp when provided.
    if (expiry) {
      payload.expiresAt = `${expiry}T00:00:00Z`;
    }

    try {
      setLoading(true);
      const res = await apiPost("catalog", "/los/credentials", payload);

      if (res?.status === "success") {
        showNotification("LOS credential created successfully", "success");
        setCreatedModal(res.data);
      } else {
        showNotification(res?.message || "Something went wrong", "error");
      }
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["los-cred__page"]}>
      {notification && (
        <div className={styles["los-cred__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["los-cred__header"]}>
        <button
          className={styles["los-cred__back-btn"]}
          onClick={onBack}
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <div className={styles["los-cred__title-group"]}>
          <h2 className={styles["los-cred__title"]}>Create LOS Credential</h2>
          <p className={styles["los-cred__subtitle"]}>
            Generate LOS API credentials for your integration.
          </p>
        </div>
      </div>

      {/* FORM */}
      <div className={styles["los-cred__form-card"]}>
        {/* Key Name */}
        <div className={styles["los-cred__form-group"]}>
          <Input
            label="Key Name"
            placeholder="e.g. los-prod-key-01"
            value={keyName}
            onChange={(e) => {
              setKeyName(e.target.value);
              setErrors((prev) => ({ ...prev, keyName: "" }));
            }}
            error={errors.keyName}
            required
          />
        </div>

        {/* Company Name — dropdown for internal users, read-only otherwise */}
        <div className={styles["los-cred__form-group"]}>
          {isAdmin ? (
            <Input
              type="select"
              label="Company Name"
              required
              placeholder="Select an organization"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setErrors((prev) => ({ ...prev, companyName: "" }));
              }}
              options={orgOptions}
              error={errors.companyName}
            />
          ) : (
            <Input label="Company Name" value={companyName} disabled />
          )}
        </div>

        {/* Products */}
        <div className={styles["los-cred__form-group"]}>
          <label className={styles["los-cred__label"]}>
            Products <span className={styles["los-cred__required-star"]}>*</span>
          </label>
          <div className={styles["los-cred__select-row"]}>
            <Input
              type="select"
              placeholder="Select a product"
              value={productPick}
              onChange={(e) => setProductPick(e.target.value)}
              options={productOptions.filter(
                (o) => !selectedProducts.some((p) => p.value === o.value),
              )}
            />
            <button
              type="button"
              className={styles["los-cred__add-btn"]}
              onClick={addProduct}
            >
              Add
            </button>
          </div>

          {selectedProducts.length > 0 && (
            <div className={styles["los-cred__ip-list"]}>
              {selectedProducts.map((p) => (
                <div key={p.value} className={styles["los-cred__ip-chip"]}>
                  {p.label}
                  <div
                    className={styles["los-cred__ip-remove"]}
                    onClick={() => removeProduct(p.value)}
                  >
                    <X size={12} strokeWidth={3} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {errors.products && (
            <p className={styles["los-cred__error-text"]}>{errors.products}</p>
          )}
        </div>

        {/* Expiry */}
        <div className={styles["los-cred__form-group"]}>
          <Input
            label="Expiry Date"
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          />
        </div>

        {/* IP Whitelist */}
        <div className={styles["los-cred__form-group"]}>
          <label className={styles["los-cred__label"]}>
            IP Whitelist{" "}
            <span className={styles["los-cred__required-star"]}>*</span>
          </label>
          <div className={styles["los-cred__ip-input-row"]}>
            <Input
              placeholder="e.g. 203.0.113.10"
              value={ipInput}
              onChange={(e) => {
                setIpInput(e.target.value);
                setErrors((prev) => ({ ...prev, ipWhitelist: "" }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addIp();
                }
              }}
            />
            <button
              type="button"
              className={styles["los-cred__add-btn"]}
              onClick={addIp}
            >
              Add
            </button>
          </div>

          {ipList.length > 0 && (
            <div className={styles["los-cred__ip-list"]}>
              {ipList.map((ip) => (
                <div key={ip} className={styles["los-cred__ip-chip"]}>
                  {ip}
                  <div
                    className={styles["los-cred__ip-remove"]}
                    onClick={() => removeIp(ip)}
                  >
                    <X size={12} strokeWidth={3} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {errors.ipWhitelist && (
            <p className={styles["los-cred__error-text"]}>
              {errors.ipWhitelist}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className={styles["los-cred__actions"]}>
          <button
            className={styles["los-cred__btn-primary"]}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Credential"}
          </button>
          <button
            className={styles["los-cred__btn-cancel"]}
            onClick={onBack}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* SECRET REVEAL MODAL */}
      {createdModal && (
        <Modal
          title="Credential Created"
          onClose={() => {
            setCreatedModal(null);
            onBack?.();
          }}
        >
          <div className={styles["los-cred__modal-form"]}>
            <p className={styles["los-cred__secret-note"]}>
              Copy your client secret now — it will not be shown again.
            </p>

            <Input label="Key Name" value={createdModal.keyName || ""} disabled />
            <Input
              label="Client ID"
              value={createdModal.clientId || ""}
              showCopy
              copyText={createdModal.clientId || ""}
              onCopySuccess={() => showNotification("Copied to clipboard")}
            />
            <Input
              label="Client Secret"
              value={createdModal.clientSecret || ""}
              showCopy
              copyText={createdModal.clientSecret || ""}
              onCopySuccess={() => showNotification("Copied to clipboard")}
            />

            {Array.isArray(createdModal.products) &&
              createdModal.products.length > 0 && (
                <div>
                  <span className={styles["los-cred__field-label"]}>
                    Products
                  </span>
                  <div className={styles["los-cred__product-chips"]}>
                    {createdModal.products.map((p) => (
                      <span
                        key={p.id}
                        className={styles["los-cred__product-chip"]}
                      >
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            <div className={styles["los-cred__modal-actions"]}>
              <Button
                text="Done"
                onClick={() => {
                  setCreatedModal(null);
                  onBack?.();
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreateLosCredential;
